/**
 * FuecList Component
 * Listado principal de FUECs con filtros, busqueda y acciones
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useUser } from "../../../UserContext";
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Dropdown,
  Card,
  Row,
  Col,
  Spin,
  message,
  Tooltip,
  Grid,
  Statistic,
  Modal,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  DownloadOutlined,
  EyeOutlined,
  MoreOutlined,
  FileTextOutlined,
  ReloadOutlined,
  FilterOutlined,
  CarOutlined,
  UserOutlined,
  CalendarOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchFuecs,
  downloadFuecPdf,
  downloadFuecsPdfUnificado,
  anularFuec,
  fetchFuecStats,
} from "./api";
import GenerateFuecForm from "./GenerateFuecForm";
import ViewFuecDialog from "./ViewFuecDialog";
import "./Fuec.css";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { useBreakpoint } = Grid;

// Ciudad y (departamento) de origen/destino guardados en el FUEC
const renderLugar = (ciudad, departamento) => {
  if (!ciudad && !departamento) return "—";
  return (
    <Space direction="vertical" size={0}>
      <span>{ciudad || "—"}</span>
      {departamento && <small style={{ color: "#888" }}>({departamento})</small>}
    </Space>
  );
};

const FuecList = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { user: currentUser } = useUser();

  // Verificación de permisos
  const hasPermission = useCallback(
    (permission) => {
      // Verificar si el módulo FUEC está disponible
      const module = currentUser?.modulos?.find((m) => m.key === "FUEC");
      if (!module || module.estado !== "disponible") return false;

      // Verificar permisos específicos
      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "FUEC" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  // Estados principales
  const [fuecs, setFuecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Paginacion
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Filtros
  const [filters, setFilters] = useState({
    search: "",
    estado: "",
    vigencia: "",
    fechaInicio: null,
    fechaFin: null,
  });

  // Modales
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedFuec, setSelectedFuec] = useState(null);
  const [anulando, setAnulando] = useState(false);

  // Cargar datos
  const loadFuecs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
      };

      if (filters.search) params.search = filters.search;
      if (filters.estado) params.estado = filters.estado;
      if (filters.vigencia) params.vigencia = filters.vigencia;
      if (filters.fechaInicio && filters.fechaFin) {
        params.fecha_inicio = filters.fechaInicio.format("YYYY-MM-DD");
        params.fecha_fin = filters.fechaFin.format("YYYY-MM-DD");
      }

      const response = await fetchFuecs(params);

      if (response.data) {
        setFuecs(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.total || response.data.length,
        }));
      } else {
        setFuecs(response);
      }
    } catch (error) {
      console.error("Error cargando FUECs:", error);
      message.error("Error al cargar los FUECs");
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchFuecStats();
      setStats(data);
    } catch (error) {
      console.error("Error cargando estadisticas:", error);
    }
  }, []);

  useEffect(() => {
    loadFuecs();
  }, [loadFuecs]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Handlers
  const handleSearch = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  const handleDateRangeChange = useCallback((dates) => {
    if (dates) {
      setFilters((prev) => ({
        ...prev,
        fechaInicio: dates[0],
        fechaFin: dates[1],
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        fechaInicio: null,
        fechaFin: null,
      }));
    }
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  const handleTableChange = useCallback((pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize,
    }));
  }, []);

  const handleView = useCallback((record) => {
    setSelectedFuec(record);
    setShowViewDialog(true);
  }, []);

  const handleDownloadPdf = useCallback(async (fuec) => {
    try {
      message.loading({ content: "Generando PDF...", key: "pdf" });
      const blob = await downloadFuecPdf(fuec.id);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `FUEC_${fuec.numero_fuec}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success({ content: "PDF descargado", key: "pdf" });
    } catch (error) {
      console.error("Error descargando PDF:", error);
      message.error({ content: "Error al descargar PDF", key: "pdf" });
    }
  }, []);

  const handleDownloadSelected = useCallback(async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Seleccione al menos un FUEC");
      return;
    }

    try {
      message.loading({ content: "Generando PDF unificado...", key: "pdf" });
      const blob = await downloadFuecsPdfUnificado(selectedRowKeys);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `FUECs_Unificado_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success({ content: "PDF unificado descargado", key: "pdf" });
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("Error descargando PDF unificado:", error);
      message.error({ content: "Error al descargar PDF", key: "pdf" });
    }
  }, [selectedRowKeys]);

  const handleAnular = useCallback(async (fuec) => {
    Modal.confirm({
      title: "Anular FUEC",
      icon: <ExclamationCircleOutlined />,
      content: `¿Esta seguro de anular el FUEC ${fuec.numero_fuec}? Esta accion no se puede deshacer.`,
      okText: "Si, anular",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        setAnulando(true);
        try {
          await anularFuec(fuec.id, "Anulado por usuario");
          message.success("FUEC anulado correctamente");
          loadFuecs();
        } catch (error) {
          console.error("Error anulando FUEC:", error);
          message.error("Error al anular el FUEC");
        } finally {
          setAnulando(false);
        }
      },
    });
  }, [loadFuecs]);

  const handleGenerateSuccess = useCallback(() => {
    setShowGenerateForm(false);
    loadFuecs();
    loadStats();
    message.success("FUEC generado exitosamente");
  }, [loadFuecs, loadStats]);

  const clearFilters = useCallback(() => {
    setFilters({
      search: "",
      estado: "",
      vigencia: "",
      fechaInicio: null,
      fechaFin: null,
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  // Columnas de la tabla
  const columns = useMemo(
    () => [
      {
        title: "No. FUEC",
        dataIndex: "numero_fuec",
        key: "numero_fuec",
        width: 200,
        fixed: "left",
        render: (text) => (
          <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
            {text}
          </span>
        ),
      },
      {
        title: "Cliente",
        dataIndex: "cliente_nombre",
        key: "cliente_nombre",
        width: 200,
        ellipsis: true,
        render: (text) => (
          <Tooltip title={text}>
            <span>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: "Origen",
        key: "origen",
        width: 170,
        render: (_, record) => renderLugar(record.origen_ciudad_nombre, record.origen_departamento_nombre),
      },
      {
        title: "Destino",
        key: "destino",
        width: 170,
        render: (_, record) => renderLugar(record.destino_ciudad_nombre, record.destino_departamento_nombre),
      },
      {
        title: "Vehiculo",
        dataIndex: "vehiculo_placa",
        key: "vehiculo_placa",
        width: 100,
        render: (text, record) => (
          <Space direction="vertical" size={0}>
            <Tag icon={<CarOutlined />} color="blue">
              {text}
            </Tag>
            <small style={{ color: "#888" }}>
              {record.vehiculo_marca} {record.vehiculo_modelo}
            </small>
          </Space>
        ),
      },
      {
        title: "Conductor",
        dataIndex: "conductor_nombre",
        key: "conductor_nombre",
        width: 180,
        ellipsis: true,
        render: (text, record) => (
          <Space direction="vertical" size={0}>
            <span>
              <UserOutlined /> {text}
            </span>
            <small style={{ color: "#888" }}>CC: {record.conductor_cedula}</small>
          </Space>
        ),
      },
      {
        title: "Vigencia",
        key: "vigencia",
        width: 180,
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <span>
              <CalendarOutlined />{" "}
              {dayjs(record.fecha_inicio_vigencia).format("DD/MM/YYYY")}
            </span>
            <span>
              al {dayjs(record.fecha_fin_vigencia).format("DD/MM/YYYY")}
            </span>
          </Space>
        ),
      },
      {
        title: "Estado",
        dataIndex: "estado",
        key: "estado",
        width: 120,
        align: "center",
        render: (estado) => {
          const config = {
            activo: { color: "green", icon: <CheckCircleOutlined />, text: "Activo" },
            vencido: { color: "orange", icon: <ClockCircleOutlined />, text: "Vencido" },
            anulado: { color: "red", icon: <StopOutlined />, text: "Anulado" },
          };
          const cfg = config[estado] || { color: "default", text: estado };
          return (
            <Tag color={cfg.color} icon={cfg.icon}>
              {cfg.text}
            </Tag>
          );
        },
      },
      {
        title: "Traslados",
        dataIndex: "traslados_count",
        key: "traslados_count",
        width: 100,
        align: "center",
        render: (count) => (
          <Tag color={count > 0 ? "blue" : "default"}>{count || 0}</Tag>
        ),
      },
      {
        title: "Acciones",
        key: "acciones",
        width: 100,
        fixed: "right",
        align: "center",
        render: (_, record) => {
          const items = [
            {
              key: "view",
              label: "Ver detalle",
              icon: <EyeOutlined />,
              onClick: () => handleView(record),
            },
            hasPermission("EXPORTAR_FUEC") && {
              key: "download",
              label: "Descargar PDF",
              icon: <DownloadOutlined />,
              onClick: () => handleDownloadPdf(record),
            },
            hasPermission("ANULAR_FUEC") && record.estado === "activo" && {
              key: "anular",
              label: "Anular",
              icon: <StopOutlined />,
              danger: true,
              onClick: () => handleAnular(record),
            },
          ].filter(Boolean);

          return (
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [handleView, handleDownloadPdf, handleAnular, hasPermission]
  );

  // Row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.estado === "anulado",
    }),
  };

  return (
    <div className="fuec-list-container">
      {/* Estadisticas */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Total FUECs"
                value={stats.total || 0}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Vigentes"
                value={stats.vigentes || 0}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Vencidos"
                value={stats.vencidos || 0}
                valueStyle={{ color: "#faad14" }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Generados Hoy"
                value={stats.hoy || 0}
                prefix={<CalendarOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Barra de acciones y filtros */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={6}>
            <Input
              placeholder="Buscar por numero, cliente, placa, código vehículo, ciudad..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Estado"
              value={filters.estado || undefined}
              onChange={(v) => handleFilterChange("estado", v)}
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="activo">Activo</Option>
              <Option value="vencido">Vencido</Option>
              <Option value="anulado">Anulado</Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Vigencia"
              value={filters.vigencia || undefined}
              onChange={(v) => handleFilterChange("vigencia", v)}
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="vigente">Vigente</Option>
              <Option value="proximo_vencer">Proximo a vencer</Option>
              <Option value="vencido">Vencido</Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <RangePicker
              value={
                filters.fechaInicio && filters.fechaFin
                  ? [filters.fechaInicio, filters.fechaFin]
                  : null
              }
              onChange={handleDateRangeChange}
              format="DD/MM/YYYY"
              style={{ width: "100%" }}
              placeholder={["Fecha inicio", "Fecha fin"]}
            />
          </Col>
          <Col xs={24} md={4}>
            <Space wrap>
              <Tooltip title="Limpiar filtros">
                <Button icon={<FilterOutlined />} onClick={clearFilters}>
                  Limpiar
                </Button>
              </Tooltip>
              <Tooltip title="Refrescar">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadFuecs}
                  loading={loading}
                />
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Acciones principales */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              {hasPermission("CREAR_FUEC") && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowGenerateForm(true)}
                >
                  {isMobile ? "Generar" : "Generar FUEC"}
                </Button>
              )}
              {hasPermission("EXPORTAR_FUEC") && selectedRowKeys.length > 0 && (
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadSelected}
                >
                  Descargar ({selectedRowKeys.length})
                </Button>
              )}
            </Space>
          </Col>
          <Col>
            <span style={{ color: "#888" }}>
              {pagination.total} registro(s)
            </span>
          </Col>
        </Row>
      </Card>

      {/* Tabla */}
      <Card bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading || anulando}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={fuecs}
            rowSelection={rowSelection}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total}`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1540 }}
            size="small"
          />
        </Spin>
      </Card>

      {/* Modal Generar FUEC */}
      <GenerateFuecForm
        visible={showGenerateForm}
        onClose={() => setShowGenerateForm(false)}
        onSuccess={handleGenerateSuccess}
      />

      {/* Modal Ver FUEC */}
      <ViewFuecDialog
        visible={showViewDialog}
        fuec={selectedFuec}
        onClose={() => {
          setShowViewDialog(false);
          setSelectedFuec(null);
        }}
        onDownload={handleDownloadPdf}
        canExport={hasPermission("EXPORTAR_FUEC")}
      />
    </div>
  );
};

export default FuecList;
